import {
	AutoSizer,
	Column,
	Table,
	type TableCellRenderer,
	type TableHeaderProps,
} from 'react-virtualized';
import { type IUser } from 'src/models';
import {
	type Theme,
	type WithStyles,
	createStyles,
	withStyles,
} from '@material-ui/core/styles';
import React from 'react';
import TableCell from '@material-ui/core/TableCell';
import clsx from 'clsx';

declare module '@material-ui/core/styles/withStyles' {
	// Augment the BaseCSSProperties so that we can control jss-rtl
	interface BaseCSSProperties {
		/*
		 * Used to control if the rule-set should be affected by rtl transformation
		 */
		flip?: boolean;
	}
}

const styles = (theme: Theme) =>
	createStyles({
		flexContainer: {
			alignItems: 'center',
			boxSizing: 'border-box',
			display: 'flex',
		},
		noClick: {
			cursor: 'initial',
		},
		table: {
			// Temporary right-to-left patch, waiting for
			// https://github.com/bvaughn/react-virtualized/issues/454
			// '& .ReactVirtualized__Table__headerRow': {
			//     flip: false,
			//     paddingRight: theme.direction === 'rtl' ? '0 !important' : undefined,
			// },
		},
		tableCell: {
			borderBottom: '1px solid rgba(214, 214, 214, .7)',
			cursor: 'pointer',
			flex: 1,
		},
		tableRow: {
			cursor: 'pointer',
		},
		tableRowHover: {
			'&:hover': {
				backgroundColor: theme.palette.grey[200],
			},
		},
	});

const inactiveStyle = {
	background: '#f2f2f2',
	color: '#ccc',
	cursor: 'auto',
	height: 48,
};

const cellStyle = {
	height: 48,
};

interface ColumnData {
	dataKey: string;
	label: string;
	numeric?: boolean;
	width: number;
}

interface Row {
	index: number;
}

interface MuiVirtualizedTableProps extends WithStyles<typeof styles> {
	columns: ColumnData[];
	headerHeight?: number;
	onRowClick?: (data: any) => void;
	rowCount: number;
	rowGetter: (row: Row) => any;
	rowHeight?: number;
	users: IUser[];
}

class MuiVirtualizedTable extends React.PureComponent<MuiVirtualizedTableProps> {
	static defaultProps = {
		headerHeight: 48,
		rowHeight: 48,
	};

	getRowClassName = ({ index }: Row) => {
		const { classes, onRowClick } = this.props;

		return clsx(classes.tableRow, classes.flexContainer, {
			[classes.tableRowHover]: index !== -1 && onRowClick !== null,
		});
	};

	cellRenderer: TableCellRenderer = ({ cellData, columnIndex, rowIndex }) => {
		const { classes, columns, onRowClick, users } = this.props;

		return (
			<TableCell
				align={
					(columnIndex !== null && columns[columnIndex].numeric) ||
						false
						? 'right'
						: 'left'
				}
				className={clsx(classes.tableCell, classes.flexContainer, {
					[classes.noClick]: onRowClick == null,
				})}
				component={'div'}
				style={cellStyle}
				variant={'body'}
			>
				{cellData}
			</TableCell>
		);
	};

	headerRenderer = ({
		columnIndex,
		label,
	}: TableHeaderProps & { columnIndex: number }) => {
		const { classes, columns, headerHeight } = this.props;

		return (
			<TableCell
				align={columns[columnIndex].numeric || false ? 'right' : 'left'}
				className={clsx(
					classes.tableCell,
					classes.flexContainer,
					classes.noClick,
				)}
				component={'div'}
				style={{ height: headerHeight }}
				variant={'head'}
			>
				<span>{label}</span>
			</TableCell>
		);
	};

	render() {
		const { classes, columns, headerHeight, rowHeight, ...tableProps } =
			this.props;

		return (
			<AutoSizer>
				{({ height, width }) => (
					<Table
						className={classes.table}
						gridStyle={{
							direction: 'inherit',
						}}
						headerHeight={headerHeight!}
						height={height}
						rowHeight={rowHeight!}
						width={width}
						{...tableProps}
						rowClassName={this.getRowClassName}
					>
						{columns.map(({ dataKey, ...other }, index) => {
							return (
								<Column
									cellRenderer={this.cellRenderer}
									className={classes.flexContainer}
									dataKey={dataKey}
									headerRenderer={(headerProps) =>
										this.headerRenderer({
											...headerProps,
											columnIndex: index,
										})
									}
									key={dataKey}
									{...other}
								/>
							);
						})}
					</Table>
				)}
			</AutoSizer>
		);
	}
}

export default withStyles(styles)(MuiVirtualizedTable);
